import { MAX_ROUTINE_SECONDS } from './routine-duration.js';
import { t } from '../../shared/i18n/index.js';

/** Persisted clocks: leaving a view, backgrounding or reloading requires no writes. */
export function createRoutineTimer({getScope, getRun, write, now = Date.now}) {
  const owner = crypto.randomUUID(), pending = new Map();
  let active = null;
  const key = value => JSON.stringify([value.scope, value.runId, value.stepId]);
  const token = value => ({seconds:value?.seconds || 0, runningAt:value?.runningAt || null, timerOwner:value?.timerOwner || ''});
  const elapsed = result => Math.round((result?.seconds || 0) + (result?.runningAt ? Math.max(0, now()-Date.parse(result.runningAt))/1000 : 0));
  function save(value, seconds, runningAt, expected=value.expected) {
    if(getScope() !== value.scope)throw Error(t('routines.error.timerOtherMap'));
    if(seconds > MAX_ROUTINE_SECONDS)throw Error(t('routines.error.timerTooLong'));
    write(value.runId,value.stepId,{seconds,runningAt,timerOwner:owner},expected);
  }
  function checkpoint(pause=false, runId=active?.runId, stepId=active?.stepId) {
    if(!runId||!stepId)return;
    const run=getRun(runId), result=run?.results[stepId];
    if(!result?.runningAt)return;
    const value={scope:getScope(),runId,stepId,title:run.steps.find(s=>s.id===stepId)?.title||run.title,base:result.seconds||0,expected:token(result)};
    const seconds=elapsed(result), stamp=pause?null:new Date(now()).toISOString();
    try {
      save(value,seconds,stamp);
      pending.delete(key(value));
      active=pause?null:{...value,expected:{seconds,runningAt:stamp,timerOwner:owner}};
    } catch(error) {
      // Preserve an explicit failed stop locally; never overwrite a newer remote clock.
      pending.set(key(value),{...value,seconds,error:error.message});
      active=null;
      throw error;
    }
  }
  function start(runId,stepId) {
    if([...pending.values()].some(p=>p.scope===getScope()))throw Error(t('routines.error.timerPending'));
    const run=getRun(runId), result=run?.results[stepId]||{};
    if(run?.state!=='active'||!run.steps.some(s=>s.id===stepId))throw Error(t('routines.error.timerResume'));
    if(result.runningAt)return; // Already running, including in another tab/device.
    const stamp=new Date(now()).toISOString();
    const value={scope:getScope(),runId,stepId,title:run.steps.find(s=>s.id===stepId).title,base:result.seconds||0,expected:token(result)};
    save(value,value.base,stamp);
    active={...value,expected:{seconds:value.base,runningAt:stamp,timerOwner:owner}};
  }
  function retry(id,addUnsaved=false) {
    const value=pending.get(id);if(!value)return;
    let seconds=value.seconds, expected=value.expected;
    if(addUnsaved) {
      const run=getRun(value.runId),result=run?.results[value.stepId];
      if(run?.state!=='active'||result?.runningAt)throw Error(t('routines.error.timerMerge'));
      seconds=(result?.seconds||0)+Math.max(0,value.seconds-value.base);expected=token(result);
    }
    save(value,seconds,null,expected);pending.delete(id);
  }
  return {
    start,checkpoint,pause:(runId,stepId)=>checkpoint(true,runId,stepId),retry,
    reconcile:()=>{if(active&&(active.scope!==getScope()||!getRun(active.runId)?.results[active.stepId]?.runningAt))active=null;},
    owns:(runId,stepId)=>!!getRun(runId)?.results[stepId]?.runningAt,
    elapsed:(run,step)=>elapsed(run.results[step.id]),
    pending:()=>[...pending].filter(([,p])=>p.scope===getScope()).map(([id,p])=>({...p,id})),
    hasUnsaved:()=>pending.size>0,
    current:()=>active&&{...active},
    clear:()=>{active=null;pending.clear();},
  };
}
